import { X, GitCompare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCompare } from '@/contexts/CompareContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { CompareModal } from './CompareModal';

const SUPABASE_URL = 'https://cyllqmkqbdilicnbdxts.supabase.co';

function getLogoUrl(logoPath: string | null): string | null {
  if (!logoPath) return null;
  if (logoPath.startsWith('http')) return logoPath;
  return `${SUPABASE_URL}/storage/v1/object/public/logos/${logoPath}`;
}

export default function CompareBar() {
  const { selectedSolutions, removeSolution, clearSolutions, isModalOpen, openModal, closeModal } = useCompare();
  const { t } = useLanguage();

  if (selectedSolutions.length === 0) return null;

  return (
    <>
      {/* Mobile version - bottom sheet style */}
      <div className="fixed bottom-0 left-0 right-0 z-40 animate-fade-in sm:hidden">
        <div className="bg-card/95 backdrop-blur-xl border-t border-border/50 shadow-2xl p-4 safe-area-pb">
          <div className="flex items-center justify-between gap-3">
            {/* Counter and solutions */}
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="flex items-center gap-2 shrink-0">
                <div className="w-9 h-9 rounded-xl bg-gradient-primary flex items-center justify-center">
                  <GitCompare className="w-4 h-4 text-white" />
                </div>
                <span className="text-sm font-semibold">{selectedSolutions.length}/5</span>
              </div>
              
              {/* Solutions avatars */}
              <div className="flex items-center -space-x-2 flex-1 min-w-0">
                {selectedSolutions.slice(0, 4).map((solution) => (
                  <div
                    key={solution.id}
                    className="relative w-8 h-8 rounded-full bg-muted border-2 border-card flex items-center justify-center overflow-hidden"
                  >
                    {solution.logo_url ? (
                      <img
                        src={getLogoUrl(solution.logo_url)!}
                        alt={solution.name}
                        className="w-full h-full object-contain p-1"
                      />
                    ) : (
                      <span className="text-xs font-medium">AI</span>
                    )}
                  </div>
                ))}
                {selectedSolutions.length > 4 && (
                  <div className="w-8 h-8 rounded-full bg-muted border-2 border-card flex items-center justify-center">
                    <span className="text-xs font-medium">+{selectedSolutions.length - 4}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 shrink-0">
              <Button
                variant="ghost"
                size="icon"
                onClick={clearSolutions}
                className="h-9 w-9 text-muted-foreground hover:text-destructive"
              >
                <X className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                disabled={selectedSolutions.length < 2}
                onClick={openModal}
                className="bg-gradient-primary hover:opacity-90 transition-opacity h-9 px-4"
              >
                {t('compare.viewComparison')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop version - floating bar */}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 animate-fade-in hidden sm:block">
        <div className="bg-card/95 backdrop-blur-xl border border-border/50 rounded-2xl shadow-2xl p-4 flex items-center gap-4">
          {/* Counter with gradient */}
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-primary flex items-center justify-center">
              <GitCompare className="w-5 h-5 text-white" />
            </div>
            <div className="text-sm">
              <span className="font-semibold text-foreground">{selectedSolutions.length}</span>
              <span className="text-muted-foreground">/5</span>
            </div>
          </div>

          {/* Separator */}
          <div className="w-px h-8 bg-border" />

          {/* Selected solutions */}
          <div className="flex items-center gap-2">
            {selectedSolutions.map((solution, index) => (
              <div
                key={solution.id}
                className="relative group flex items-center gap-2 bg-muted/50 rounded-xl px-3 py-2 animate-scale-in hover:bg-muted transition-colors"
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                {solution.logo_url ? (
                  <img
                    src={getLogoUrl(solution.logo_url)!}
                    alt={solution.name}
                    className="w-6 h-6 object-contain rounded"
                  />
                ) : (
                  <div className="w-6 h-6 bg-gradient-primary rounded flex items-center justify-center text-xs text-white font-medium">
                    AI
                  </div>
                )}
                <span className="text-sm font-medium max-w-[80px] truncate">
                  {solution.name}
                </span>
                <button
                  onClick={() => removeSolution(solution.id)}
                  className="ml-1 p-1 rounded-full hover:bg-destructive/20 transition-colors"
                >
                  <X className="w-3.5 h-3.5 text-muted-foreground hover:text-destructive" />
                </button>
              </div>
            ))}
          </div>

          {/* Separator */}
          <div className="w-px h-8 bg-border" />

          {/* Actions */}
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={clearSolutions}
              className="text-muted-foreground hover:text-destructive"
            >
              {t('compare.clear')}
            </Button>
            <Button
              size="sm"
              disabled={selectedSolutions.length < 2}
              onClick={openModal}
              className="bg-gradient-primary hover:opacity-90 transition-opacity"
            >
              {t('compare.viewComparison')}
            </Button>
          </div>
        </div>
      </div>

      {/* Compare Modal */}
      <CompareModal isOpen={isModalOpen} onClose={closeModal} />
    </>
  );
}
